import React from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { PrimaryButton, Link } from 'components/buttons';
import { TextInputField, SelectField, MarkdownEditorField, TagInputField } from 'components/forms';
import {
  TODO_BODY_FEATURES,
  TODO_BODY_MAX_LENGTH,
  TODO_PRIORITY_OPTIONS,
  TODO_STATUS_OPTIONS,
  TODO_PRIORITY,
  TODO_STATUS
} from 'constants/todos';
import { normalizeTagNames } from 'constants/tags';
import { toMarkdownString } from 'utils/markdown/markdown';

export default function TodoForm({
  defaultValues,
  availableTagNames = [],
  onSubmit,
  loading,
  saving,
  submitLabel = 'Create',
  cancelLink = '/todos',
}) {
  const tagNames = normalizeTagNames(defaultValues?.tagNames || defaultValues?.tags);
  const { register, handleSubmit, control, formState: { errors } } = useForm({
    defaultValues: {
      priority: TODO_PRIORITY.LOW,
      status: TODO_STATUS.INPROGRESS,
      ...defaultValues,
      tagNames,
      body: toMarkdownString(defaultValues?.body)
    },
  });

  const nameValue = useWatch({ control, name: 'name' });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <TextInputField
        name="name"
        labelText="Name"
        register={register}
        registerOptions={{ required: 'Name is required' }}
        errors={errors}
        placeholder="Buy groceries"
        maxLength={120}
        showCount
        currentLength={nameValue?.length || 0}
        loading={loading}
        disabled={saving}
      />

      <MarkdownEditorField
        control={control}
        name="body"
        labelText="Description"
        placeholder="Optional details about this todo"
        features={TODO_BODY_FEATURES}
        maxLength={TODO_BODY_MAX_LENGTH}
        helperText="Only bold, italic, bulleted lists, and numbered lists are stored."
        loading={loading}
        rows={6}
        disabled={saving}
        rules={{
          maxLength: {
            value: TODO_BODY_MAX_LENGTH,
            message: `Description must be ${TODO_BODY_MAX_LENGTH} characters or fewer`
          }
        }}
      />

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <Controller
          name="priority"
          control={control}
          render={({ field }) => (
            <SelectField
              label="Priority"
              options={TODO_PRIORITY_OPTIONS}
              loading={loading}
              disabled={saving}
              {...field}
            />
          )}
        />

        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <SelectField
              label="Status"
              options={TODO_STATUS_OPTIONS}
              loading={loading}
              disabled={saving}
              {...field}
            />
          )}
        />
      </div>

      <Controller
        name="tagNames"
        control={control}
        render={({ field }) => (
          <TagInputField
            labelText="Tags"
            value={field.value}
            availableTagNames={availableTagNames}
            onChange={field.onChange}
            error={errors.tagNames?.message}
            disabled={saving}
          />
        )}
      />

      <div className="flex items-center justify-end gap-x-6">
        <Link to={cancelLink} className="text-sm font-semibold leading-6 text-white" loading={loading} disabled={saving}>
          Cancel
        </Link>
        <PrimaryButton type="submit" loading={loading} saving={saving}>
          {submitLabel}
        </PrimaryButton>
      </div>
    </form>
  );
}
